import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error,
  Refresh,
  ExpandMore,
  ExpandLess,
  Storage,
  Memory,
  Schedule,
  Computer
} from '@mui/icons-material';
import { healthCheck, HealthStatus } from '../../services/healthCheck';
import { formatTime } from '../../utils/formatters';

interface HealthStatusIndicatorProps {
  showDetails?: boolean;
  position?: 'bottom-left' | 'bottom-right' | 'bottom-center';
  size?: 'small' | 'medium' | 'large';
}

const HealthStatusIndicator: React.FC<HealthStatusIndicatorProps> = ({
  showDetails = false,
  position = 'bottom-center',
  size = 'small'
}) => {
  const [status, setStatus] = useState<HealthStatus>({ status: 'checking' });
  const [expanded, setExpanded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    // Start health monitoring
    healthCheck.start();

    // Subscribe to status changes
    const unsubscribe = healthCheck.subscribe((newStatus) => {
      setStatus(newStatus);
    });

    // Cleanup
    return () => {
      unsubscribe();
      healthCheck.stop();
    };
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await healthCheck.checkHealth();
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusIcon = (statusType: HealthStatus['status']) => {
    switch (statusType) {
      case 'healthy':
        return <CheckCircle sx={{ color: '#4caf50' }} />;
      case 'degraded':
        return <Warning sx={{ color: '#ff9800' }} />;
      case 'unhealthy':
        return <Error sx={{ color: '#f44336' }} />;
      case 'checking':
        return <Refresh sx={{ color: '#2196f3' }} />;
      case 'offline':
      default:
        return <Error sx={{ color: '#757575' }} />;
    }
  };


  const getStatusText = (statusType: HealthStatus['status']) => {
    switch (statusType) {
      case 'healthy':
        return 'Healthy';
      case 'degraded':
        return 'Degraded';
      case 'unhealthy':
        return 'Unhealthy';
      case 'checking':
        return 'Checking...';
      case 'offline':
      default:
        return 'Offline';
    }
  };

  const getPositionStyles = () => {
    switch (position) {
      case 'bottom-left':
        return { position: 'fixed' as const, bottom: 16, left: 16 };
      case 'bottom-right':
        return { position: 'fixed' as const, bottom: 16, right: 16 };
      case 'bottom-center':
      default:
        return { position: 'fixed' as const, bottom: 16, left: '50%', transform: 'translateX(-50%)' };
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'large':
        return { minWidth: 300 };
      case 'medium':
        return { minWidth: 200 };
      case 'small':
      default:
        return { minWidth: 150 };
    }
  };

  const statusSummary = healthCheck.getStatusSummary();

  return (
    <Box
      sx={{
        ...getPositionStyles(),
        zIndex: 1000,
        ...getSizeStyles(),
      }}
    >
      {/* Main Status Indicator */}
      <Tooltip title={statusSummary.message} arrow>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 2,
            px: 2,
            py: 1,
            boxShadow: 2,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${statusSummary.color}20`,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
              boxShadow: 4,
            }
          }}
          onClick={() => setExpanded(!expanded)}
        >
          {getStatusIcon(status.status)}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: statusSummary.color,
              minWidth: 60,
            }}
          >
            {getStatusText(status.status)}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRefresh();
            }}
            disabled={isRefreshing}
            sx={{
              ml: 'auto',
              opacity: 0.7,
              '&:hover': { opacity: 1 },
            }}
          >
            <Refresh
              sx={{
                fontSize: 16,
                animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>
          {showDetails && (
            <IconButton size="small">
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Box>
      </Tooltip>

      {/* Detailed Status Panel */}
      {showDetails && (
        <Collapse in={expanded}>
          <Box
            sx={{
              mt: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 2,
              p: 2,
              boxShadow: 3,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${statusSummary.color}20`,
            }}
          >
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              System Status
            </Typography>
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {statusSummary.details}
            </Typography>

            {status.services && (
              <List dense sx={{ py: 0 }}>
                {/* Database Status */}
                {status.services.database && (
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Storage sx={{ fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Database"
                      secondary={`${status.services.database.connection} • ${status.services.database.responseTime}ms`}
                      primaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip
                      label={status.services.database.status}
                      size="small"
                      color={status.services.database.status === 'healthy' ? 'success' : 'error'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </ListItem>
                )}

                {/* Server Status */}
                {status.services.server && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Computer sx={{ fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Server"
                        secondary={`PID: ${status.services.server.pid} • ${status.services.server.nodeVersion}`}
                        primaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <Chip
                        label={status.services.server.status}
                        size="small"
                        color={status.services.server.status === 'healthy' ? 'success' : 'error'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </ListItem>

                    {/* Memory Usage */}
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Memory sx={{ fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Memory"
                        secondary={`Heap: ${status.services.server.memory.heapUsed}MB / ${status.services.server.memory.heapTotal}MB`}
                        primaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <Chip
                        label={status.checks?.memory ? 'OK' : 'High'}
                        size="small"
                        color={status.checks?.memory ? 'success' : 'warning'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </ListItem>

                    {/* Uptime */}
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Schedule sx={{ fontSize: 16 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Uptime"
                        secondary={status.uptime?.human || 'Unknown'}
                        primaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <Chip
                        label={status.checks?.uptime ? 'Stable' : 'Recent'}
                        size="small"
                        color={status.checks?.uptime ? 'success' : 'info'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </ListItem>
                  </>
                )}
              </List>
            )}

            {/* Last Check Time */}
            {status.timestamp && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1, textAlign: 'center' }}
              >
                Last checked: {formatTime(status.timestamp)}
              </Typography>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default HealthStatusIndicator;
